import { useState } from 'react';
import { api, tokens } from '../lib/api';
import { PRODUCT_CATEGORY, UNIT, UNIT_LABEL, type ProductCategory, type Unit } from '@artmozaik/shared';

type Kalem = { kod: string; adet: number | null; iskonto: number | null };

/**
 * HIZLI ÜRÜN TANIMI — şartname 4.3: "bilinmeyen kod → hızlı ürün tanımı
 * modalı (ad, liste fiyatı, görsel URL — bir daha sormaz)".
 *
 * Teklif ortasında tam ürün formu açmak akışı kesiyor; burada yalnız teklifin
 * ihtiyacı olan alanlar var. Kaydedilen ürün gerçek bir ART kartı olur, bir
 * dahaki yapıştırmada kendiliğinden gelir.
 */
export default function HizliUrun({
  kalem, kapat, eklendi,
}: {
  kalem: Kalem;
  kapat: () => void;
  eklendi: (satir: {
    productId: number; code: string; name: string; imageUrl: string | null;
    listPriceKurus: number; discountPct: number; vatPct: number; qty: number;
    tavan: number | null;
  }) => void;
}) {
  const [name, setName] = useState('');
  const [listPriceTl, setListPriceTl] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [category, setCategory] = useState<ProductCategory>('ALSAT');
  const [unit, setUnit] = useState<Unit>('ADET');
  const [maxDiscountPct, setMaxDiscountPct] = useState('');
  const [vatPct, setVatPct] = useState('20');
  const [hata, setHata] = useState<string | null>(null);
  const [bekliyor, setBekliyor] = useState(false);
  const [gorselYukleniyor, setGorselYukleniyor] = useState(false);
  const [gorselBozuk, setGorselBozuk] = useState(false);

  /** Gözat ile seçilen ya da panodan yapıştırılan görseli hemen yükler; kutuya adresi yazar. */
  async function gorselYukle(dosya: File) {
    setHata(null); setGorselYukleniyor(true); setGorselBozuk(false);
    try {
      const fd = new FormData();
      fd.append('dosya', dosya, dosya.name || 'gorsel.png');
      const res = await fetch('/api/gorsel', {
        method: 'POST',
        headers: tokens.access ? { authorization: `Bearer ${tokens.access}` } : {},
        body: fd,
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j.error || 'Görsel yüklenemedi');
      setImageUrl(j.url);
    } catch (e) {
      setHata(e instanceof Error ? e.message : 'Görsel yüklenemedi');
    } finally {
      setGorselYukleniyor(false);
    }
  }

  async function gonder(e: React.FormEvent) {
    e.preventDefault();
    setHata(null); setBekliyor(true);
    try {
      const r = await api<{ id: number; code: string }>('/api/urunler', {
        method: 'POST',
        body: JSON.stringify({
          code: kalem.kod, name, category, unit,
          listPriceTl: listPriceTl ? Number(listPriceTl) : undefined,
          imageUrl: imageUrl || undefined,
          maxDiscountPct: maxDiscountPct ? Number(maxDiscountPct) : undefined,
          vatPct: Number(vatPct),
          notes: 'Teklif ekranından hızlı tanımlandı',
        }),
      });
      const tavan = maxDiscountPct ? Number(maxDiscountPct) : null;
      eklendi({
        productId: r.id, code: r.code, name,
        imageUrl: imageUrl || null,
        listPriceKurus: Math.round(Number(listPriceTl || 0) * 100),
        discountPct: kalem.iskonto ?? tavan ?? 0,
        vatPct: Number(vatPct),
        qty: kalem.adet ?? 1,
        tavan,
      });
    } catch (err) {
      setHata(err instanceof Error ? err.message : 'Ürün eklenemedi');
    } finally {
      setBekliyor(false);
    }
  }

  return (
    <div className="ccd-drawer-arka" onClick={kapat} role="presentation">
      <aside className="ccd-drawer" onClick={(e) => e.stopPropagation()}
             role="dialog" aria-modal="true" aria-label="Hızlı ürün tanımı">
        <form className="ccd-stack" onSubmit={gonder}>
          <div className="ccd-row-between">
            <div>
              <div className="ccd-eyebrow">Teklif</div>
              <h2 className="ccd-heading">Hızlı ürün tanımı</h2>
            </div>
            <button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-sm" onClick={kapat}>
              Atla
            </button>
          </div>

          <div className="ccd-banner">
            <span className="ccd-banner-icon" aria-hidden="true">?</span>
            <div className="ccd-meta">
              <b className="ccd-mono">{kalem.kod}</b> kodu tanınmadı. Burada tanımlanınca
              gerçek bir ürün kartı olur — <b>bir daha sorulmaz</b>.
            </div>
          </div>

          <div className="ccd-field">
            <label className="ccd-field-label" htmlFor="had">
              Ürün adı<span className="ccd-required"> *</span>
            </label>
            <input id="had" className="ccd-input" required autoFocus
                   value={name} onChange={(e) => setName(e.target.value)}
                   placeholder="Grohe Rainshower duş sistemi" />
          </div>

          <div className="ccd-form-grid">
            <div className="ccd-field">
              <label className="ccd-field-label" htmlFor="hfiyat">Liste fiyatı (TL)</label>
              <input id="hfiyat" className="ccd-input" type="number" inputMode="decimal" step="any"
                     value={listPriceTl} onChange={(e) => setListPriceTl(e.target.value)} />
            </div>
            <div className="ccd-field">
              <label className="ccd-field-label" htmlFor="htavan">İskonto tavanı %</label>
              <input id="htavan" className="ccd-input" type="number" inputMode="decimal" step="any" min={0} max={100}
                     value={maxDiscountPct} onChange={(e) => setMaxDiscountPct(e.target.value)} />
              <div className="ccd-field-hint">
                Boş bırakılırsa markasının alış iskontosundan türetilir.
              </div>
            </div>
          </div>

          <div className="ccd-field">
            <label className="ccd-field-label" htmlFor="hgorsel">Görsel</label>
            <div className="ccd-row" style={{ alignItems: 'stretch' }}>
              <input id="hgorsel" className="ccd-input" type="url" style={{ flex: 1 }}
                     placeholder="Adres yapıştırın ya da görseli buraya yapıştırın (Ctrl+V)"
                     value={imageUrl}
                     onChange={(e) => { setImageUrl(e.target.value.trim()); setGorselBozuk(false); }}
                     onPaste={(e) => {
                       const f = [...e.clipboardData.files].find((x) => x.type.startsWith('image/'));
                       if (f) { e.preventDefault(); void gorselYukle(f); }
                     }} />
              <label className="ccd-btn" style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>
                {gorselYukleniyor ? 'Yükleniyor…' : 'Gözat…'}
                <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" hidden
                       disabled={gorselYukleniyor}
                       onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void gorselYukle(f); }} />
              </label>
            </div>
            {imageUrl && !gorselBozuk && (
              <div className="ccd-row" style={{ marginTop: 8 }}>
                <img src={imageUrl} alt="Ürün görseli önizleme" onError={() => setGorselBozuk(true)}
                     style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--ccd-border)' }} />
                <button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-sm" onClick={() => setImageUrl('')}>Kaldır</button>
              </div>
            )}
            {imageUrl && gorselBozuk && (
              <div className="ccd-field-hint" style={{ color: 'var(--ccd-danger)' }}>
                Bu adresteki görsel açılamadı. Adresi kontrol edin ya da Gözat ile bilgisayardan yükleyin.
              </div>
            )}
            <div className="ccd-field-hint">Teklif PDF'inde satırın yanında görünür. Bilgisayardan seçebilir, adres ya da kopyalanmış görsel yapıştırabilirsiniz.</div>
          </div>

          <div className="ccd-form-grid">
            <div className="ccd-field">
              <label className="ccd-field-label" htmlFor="hkat">Kategori</label>
              <select id="hkat" className="ccd-select" value={category}
                      onChange={(e) => setCategory(e.target.value as ProductCategory)}>
                {PRODUCT_CATEGORY.map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
            </div>
            <div className="ccd-field">
              <label className="ccd-field-label" htmlFor="hbirim">Birim</label>
              <select id="hbirim" className="ccd-select" value={unit}
                      onChange={(e) => setUnit(e.target.value as Unit)}>
                {UNIT.map((u) => <option key={u} value={u}>{UNIT_LABEL[u]}</option>)}
              </select>
            </div>
          </div>

          <div className="ccd-field">
            <label className="ccd-field-label" htmlFor="hkdv">KDV %</label>
            <input id="hkdv" className="ccd-input" type="number" inputMode="decimal" step="any" min={0} max={100}
                   style={{ maxWidth: 120 }}
                   value={vatPct} onChange={(e) => setVatPct(e.target.value)} />
          </div>

          {hata && (
            <div className="ccd-banner ccd-banner-danger">
              <span className="ccd-banner-icon" aria-hidden="true">!</span><span>{hata}</span>
            </div>
          )}

          <button className="ccd-btn ccd-btn-primary ccd-btn-lg" disabled={bekliyor}>
            {bekliyor ? 'Kaydediliyor…' : 'Tanımla ve teklife ekle'}
          </button>
        </form>
      </aside>
    </div>
  );
}
